import React from 'react';

import { useFormContext } from 'react-hook-form';

import Button from '@asteria/component-core/button';
import { Text } from '@asteria/component-core/typography';
import Wrapper, {
	Content,
	Footer,
	FooterSection,
	Header,
} from '@asteria/component-core/wrapper';

import Alert from '@asteria/component-alert';
import { Wrapper as FormWrapper, Input } from '@asteria/component-form';

import { useTranslate } from '@asteria/language';

import AuthStepper from '../components/authStepper';
import { AuthContext } from '../context';

const STEPS = ['ACCOUNT', 'COMPANY', 'SECURITY'];
const STEP_CONFIG = [
	{ id: 'ACCOUNT', label: 'signup.steps.account' },
	{ id: 'COMPANY', label: 'signup.steps.company' },
	{ id: 'SECURITY', label: 'signup.steps.security' },
];

const Step1 = React.memo(function Step1() {
	const { signupCode } = React.useContext(AuthContext);
	const { t } = useTranslate();

	return [
		<Input
			key="username"
			type="email"
			name="user.username"
			label={t('signup.email.label', {
				postfix: { required: true },
			})}
			placeholder={t('signup.email.placeholder')}
			required
			direction="vertical"
		/>,
		<Input
			key="phone"
			type="phone"
			name="user.settings.phone"
			label={t('signup.phone.label')}
			placeholder={t('signup.phone.placeholder')}
			direction="vertical"
		/>,
		signupCode ? (
			<Input
				key="code"
				type="text"
				name="signupKey"
				label={t('signup.invitationCode.label', {
					postfix: { required: true },
				})}
				placeholder={t('signup.invitationCode.placeholder')}
				required
				direction="vertical"
			/>
		) : null,
	];
});

const Step2 = React.memo(function Step2() {
	const { t } = useTranslate();

	return [
		<Input
			key="name"
			type="text"
			name="company.name"
			label={t('signup.company.label', {
				postfix: { required: true },
			})}
			placeholder={t('signup.company.placeholder')}
			required
			direction="vertical"
			autoFocus
		/>,
		<Input
			key="orgnumber"
			type="text"
			name="company.orgnumber"
			label={t('signup.organizationNumber.label')}
			placeholder={t('signup.organizationNumber.placeholder')}
			direction="vertical"
		/>,
		<Input
			key="fullName"
			type="text"
			name="user.fullName"
			label={t('signup.fullName.label')}
			placeholder={t('signup.fullName.placeholder')}
			direction="vertical"
		/>,
	];
});

const Step3 = React.memo(function Step3() {
	const { t } = useTranslate();

	return [
		<Input
			key="password"
			type="password"
			name="user.password"
			label={t('signup.password.label', {
				postfix: { required: true },
			})}
			placeholder={t('signup.password.placeholder')}
			required
			direction="vertical"
			autoFocus
		/>,
		<Input
			key="password2"
			type="password"
			name="user.passwordConfirm"
			label={t('signup.rePassword.label', {
				postfix: { required: true },
			})}
			placeholder={t('signup.rePassword.placeholder')}
			required
			direction="vertical"
		/>,
	];
});

const SignUpArea = ({ error, step, back, update, next, loading }) => {
	const { t } = useTranslate();
	const { logo, signupCode } = React.useContext(AuthContext);
	const { trigger, getValues, setError } = useFormContext();
	const onStepClick = React.useCallback(
		async (stepId) => {
			if (stepId === step) return;

			// Going backwards is always allowed
			const currentIndex = STEPS.indexOf(step || 'ACCOUNT');
			const targetIndex = STEPS.indexOf(stepId);
			if (targetIndex < currentIndex) {
				return update?.({
					type: 'NEXT',
					payload: {
						step: stepId,
					},
				});
			}

			// Going forward requires validation
			let valid = true;

			if (step === null || step === 'ACCOUNT') {
				valid = await trigger(
					['user.username', signupCode ? 'signupKey' : null].filter(
						Boolean,
					),
				);
			} else if (step === 'COMPANY') {
				valid = await trigger(['company.name']);
			} else if (step === 'SECURITY') {
				valid = await trigger([
					'user.password',
					'user.passwordConfirm',
				]);
				if (valid) {
					const [password, password2] = getValues([
						'user.password',
						'user.passwordConfirm',
					]);
					if (password !== password2) {
						valid = false;
						setError('user.passwordConfirm', {
							type: 'error',
							message: t([
								'signup.password.error.mismatch',
								'auth.signup.password.error.mismatch',
							]),
						});
					}
				}
			}

			if (valid) {
				update?.({
					type: 'NEXT',
					payload: {
						step: stepId,
					},
				});
			}
		},
		[getValues, setError, signupCode, step, t, trigger, update],
	);
	return (
		<div className="asteria-component__sign-up">
			<Wrapper scroll>
				<Header logo={logo} onBack={back} verticalAlign="center">
					{t(['auth.title', `signup.title`])}
				</Header>
				<Content scroll>
					{error ? (
						<Alert level="error">
							<Text>{error}</Text>
						</Alert>
					) : null}
					<AuthStepper
						steps={STEP_CONFIG}
						currentStep={step || 'ACCOUNT'}
						onStepClick={onStepClick}
						className="mb-2"
					/>
					<FormWrapper>
						<Content>
							{step === null ? <Step1 /> : null}
							{step === 'ACCOUNT' ? <Step1 /> : null}
							{step === 'COMPANY' ? <Step2 /> : null}
							{step === 'SECURITY' ? <Step3 /> : null}
						</Content>
					</FormWrapper>
				</Content>
				<Footer>
					<FooterSection position="first">
						<Button
							label={t(['action.back', `signup.action.back`])}
							variant="secondary"
							onClick={back}
						/>
					</FooterSection>

					<FooterSection position="last">
						{step === null || step === 'ACCOUNT' ? (
							<Button
								label={t(['action.next', `signup.action.next`])}
								variant="primary"
								onClick={next}
								loading={loading}
								disabled={loading}
							/>
						) : null}

						{step === 'COMPANY' ? (
							<Button
								label={t(['action.next', `signup.action.next`])}
								variant="primary"
								onClick={next}
								loading={loading}
								disabled={loading}
							/>
						) : null}

						{step === 'SECURITY' ? (
							<Button
								type="submit"
								label={t([
									'action.submit',
									`signup.action.submit`,
								])}
								variant="primary"
								loading={loading}
								disabled={loading}
							/>
						) : null}
					</FooterSection>
				</Footer>
			</Wrapper>
		</div>
	);
};

export default SignUpArea;
